import { guidelinesHttp } from '@/lib/api/guidelines-client';
import { describeApiFailure } from '@/shared/api/describeApiFailure';

/**
 * Hand-typed rather than routed through the generated `@inference.engine/guidelines-api-client`:
 * `/brand-font` is a new controller and the client is regenerated from a built `guidelines-api`,
 * which is a separate step (see `libs/http-clients/CLAUDE.md`) not yet run for this route. Calling
 * `guidelinesHttp` directly is exactly what the generated `BrandFontService` would do once it
 * exists -- these calls should be replaced by it once the client is regenerated.
 */
export interface BrandFontItem {
  id: string;
  assetId: string;
  filename: string;
  familyName: string | null;
  weight: string | null;
  style: string | null;
  /** `woff2`, `woff`, `ttf` or `otf`. */
  format: string | null;
  sizeBytes: number;
  /** The folder this is filed in, the same tree brand media files into. */
  folderId: string | null;
  /**
   * What the file's own OpenType `name` table said about itself, read by the uploader.
   *
   * Null where the file declared nothing, never guessed at. Fonts added before the uploader
   * started reading these carry nulls until the same file is uploaded again.
   */
  foundry: string | null;
  designer: string | null;
  copyright: string | null;
  licenseDescription: string | null;
  licenseUrl: string | null;
  version: string | null;
  /** SHA-256 of the file's bytes. Null for a font added before it was recorded. */
  contentHash: string | null;
  tags: Record<string, string[]>;
  createdAt: string;
}

/**
 * Narrows to folders, the same two parameters the media listing takes.
 *
 * A type rather than an interface so it satisfies the client's `Record<string, unknown>` query
 * parameter, which an interface cannot: only a type alias gets an implicit index signature.
 */
export type ListBrandFontsQuery = {
  folderIds?: readonly string[];
  folderScope?: 'direct' | 'subtree';
  pageSize?: number;
  cursor?: string;
};

/** The endpoint's own ceiling. Fewer, larger pages is the cheaper way to walk one library. */
const PAGE_SIZE = 200;

/**
 * A guard, not a page budget: the loop ends on a null cursor. This only stops a server that keeps
 * handing back a cursor from spinning here forever.
 */
const MAX_PAGES = 50;

/**
 * Every font the scope holds, followed across pages.
 *
 * The first page alone used to be enough, on the reasoning that a pack's own families would be
 * among the most recent uploads. That expires the moment somebody imports another brand system:
 * the list is newest-first, so a library past a few hundred cuts pushes an older pack's faces off
 * page one, `usePackFonts` finds none of the families it was asked for, and every board of that
 * brand silently measures and wraps on whatever face the browser substitutes. A pack that draws
 * type as outlines stops composing altogether, because the engine has no glyph geometry to give it.
 *
 * So this walks the cursor to the end. It is the whole library rather than the families asked for
 * because the endpoint offers no name filter; one query serves every caller and TanStack caches it.
 */
export const listBrandFonts = async (query: ListBrandFontsQuery = {}): Promise<BrandFontItem[]> => {
  const fonts: BrandFontItem[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await guidelinesHttp.get({
      security: [{ scheme: 'bearer', type: 'http' }],
      url: '/api/v1/brand-font',
      query: { ...query, pageSize: PAGE_SIZE, ...(cursor === undefined ? {} : { cursor }) },
    });

    if (!response.data) {
      throw new Error(describeApiFailure(response.error, 'The fonts could not be loaded.'));
    }

    const envelope = response.data as unknown as {
      data: BrandFontItem[];
      nextCursor?: string | null;
    };
    fonts.push(...envelope.data);
    if (!envelope.nextCursor) {
      break;
    }
    cursor = envelope.nextCursor;
  }

  return fonts;
};

export interface CreateBrandFontInput {
  assetId: string;
  filename: string;
  familyName?: string;
  weight?: string;
  style?: string;
  format?: string;
  sizeBytes?: number;
  /**
   * Read out of the file here rather than by the server: the bytes are already in the browser's
   * hands at upload, and re-fetching them to read six strings would cost a download per font.
   */
  foundry?: string;
  designer?: string;
  copyright?: string;
  licenseDescription?: string;
  licenseUrl?: string;
  version?: string;
  contentHash?: string;
  folderId?: string;
}

export const createBrandFont = async (input: CreateBrandFontInput): Promise<BrandFontItem> => {
  const response = await guidelinesHttp.post({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/brand-font',
    body: input,
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.data) {
    throw new Error(
      describeApiFailure(response.error, 'The font could not be added to the library.'),
    );
  }

  return response.data as unknown as BrandFontItem;
};

/** A new file for a font already in the library. Where it is filed stays as it was. */
export type ReplaceBrandFontFileInput = Omit<CreateBrandFontInput, 'folderId'> & {
  fontId: string;
};

/**
 * Puts a new file behind an existing font, keeping its id, folder and tags. Refused with a 409
 * when another font in the library is already that file.
 */
export const replaceBrandFontFile = async ({
  fontId,
  ...body
}: ReplaceBrandFontFileInput): Promise<BrandFontItem> => {
  const response = await guidelinesHttp.patch({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/brand-font/{fontId}/file',
    path: { fontId },
    body,
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The font file could not be replaced.'));
  }

  return response.data as unknown as BrandFontItem;
};

export interface SetFontTagsInput {
  fontId: string;
  tags: Record<string, string[]>;
}

export const setFontTags = async ({ fontId, tags }: SetFontTagsInput): Promise<BrandFontItem> => {
  const response = await guidelinesHttp.patch({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/brand-font/{fontId}/tags',
    path: { fontId },
    body: { tags },
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The font could not be tagged.'));
  }

  return response.data as unknown as BrandFontItem;
};

/**
 * Retires fonts. Soft, matching brand media: the row is kept and stops being offered, so a piece
 * that already set type in it keeps rendering.
 *
 * An id that matched nothing is left alone rather than failing the call: a selection is one
 * gesture, and losing all of it over one font another tab already deleted would throw away the
 * rest of the person's work.
 */
export const archiveBrandFonts = async (
  fontIds: readonly string[],
): Promise<{ archived: number }> => {
  const response = await guidelinesHttp.post({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/brand-font/actions/archive',
    body: { fontIds },
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The fonts could not be deleted.'));
  }

  return response.data as unknown as { archived: number };
};
